/**
 * The reporting calendar (CHG-2026-09-manager-reporting, D4).
 *
 * Every date the reporting endpoints accept as a bare `YYYY-MM-DD` is a **Vienna** calendar day, so
 * the browser must name the day in that zone rather than in the viewer's own. A manager opening the
 * dashboard from another timezone still gets his store's day, and the boundary does not move when
 * he travels.
 */
export const REPORT_TIME_ZONE = 'Europe/Vienna'

// en-CA formats as YYYY-MM-DD, which is exactly the wire format — no manual padding, and no
// dependency on the viewer's locale for a machine-readable value.
const isoDateInZone = new Intl.DateTimeFormat('en-CA', {
  timeZone: REPORT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** The Vienna calendar day of an instant, as `YYYY-MM-DD`. */
export function reportDate(at: Date): string {
  return isoDateInZone.format(at)
}

/** `YYYY-MM-DD` shifted by whole days, staying on the Vienna calendar. */
export function shiftReportDate(date: string, days: number): string {
  // Noon UTC as the anchor: far enough from either midnight that no daylight-saving shift can move
  // the date by one while adding whole days.
  const anchor = new Date(`${date}T12:00:00Z`)
  anchor.setUTCDate(anchor.getUTCDate() + days)
  return reportDate(anchor)
}

/** Whole days between two `YYYY-MM-DD` values, inclusive of both ends. */
export function reportDaySpan(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00Z`)
  const b = Date.parse(`${to}T12:00:00Z`)
  return Math.round((b - a) / 86_400_000) + 1
}

const hourInZone = new Intl.DateTimeFormat('en-GB', {
  timeZone: REPORT_TIME_ZONE,
  hour: '2-digit',
  hour12: false,
})

/**
 * The current Vienna hour, 0–23.
 *
 * The browser's own `getHours()` would be the viewer's hour, and the running-hour marker and the
 * forecast both have to line up with the server's Vienna buckets — otherwise a manager checking the
 * dashboard from another timezone gets a "now" line in the wrong place.
 */
export function reportHour(at: Date): number {
  // en-GB with hour12 false renders midnight as "24" in some engines; both map to 0.
  const raw = Number(hourInZone.format(at))
  return raw === 24 ? 0 : raw
}

const minuteInZone = new Intl.DateTimeFormat('en-GB', {
  timeZone: REPORT_TIME_ZONE,
  minute: '2-digit',
})

/**
 * How far the current Vienna hour has run, as a fraction of it — 0.5 at half past.
 *
 * The end-of-day estimate counts the running hour on both sides at this fraction. Read in Vienna
 * rather than from `getMinutes()`, for the same reason as the hour: a zone can be offset by half an
 * hour, and then the local minute is not the store's minute.
 */
export function reportHourFraction(at: Date): number {
  return Number(minuteInZone.format(at)) / 60
}

/** The ISO weekday (1 = Monday) of a Vienna calendar day. */
export function reportWeekday(date: string): number {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay()
  return day === 0 ? 7 : day
}

const offsetInZone = new Intl.DateTimeFormat('en-GB', {
  timeZone: REPORT_TIME_ZONE,
  timeZoneName: 'longOffset',
})

/**
 * A Vienna wall-clock moment as an ISO instant — `2026-09-04` plus `18:00` becomes
 * `2026-09-04T16:00:00.000Z`.
 *
 * The endpoint accepts either a bare `YYYY-MM-DD` (a whole Vienna day) or a full instant, so a
 * period carrying a time has to be converted here rather than sent as a naive local string: the
 * server would otherwise read the browser's wall clock as Vienna's, and the same report requested
 * from another timezone would quietly cover different hours than the one requested in the store.
 *
 * `24:00` means the end of the given day, not midnight of the next one — that is what "bis 24:00"
 * means to a reader, and it keeps the window closed on the day they actually picked.
 */
export function reportInstant(date: string, time: string): string {
  const [rawHour, rawMinute = '0'] = time.split(':')
  const hour = Number(rawHour)
  const minute = Number(rawMinute)
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return reportInstant(date, '00:00')
  }
  const rollsOver = hour >= 24
  const day = rollsOver ? shiftReportDate(date, 1) : date
  const localHour = rollsOver ? 0 : hour

  const asUtc = Date.parse(`${day}T${pad(localHour)}:${pad(minute)}:00.000Z`)
  // The offset is read AT that moment, not now: a range spanning the daylight-saving switch would
  // otherwise be an hour out at one end.
  const offsetMinutes = zoneOffsetMinutes(new Date(asUtc))
  const instant = asUtc - offsetMinutes * 60_000 - (rollsOver ? 1 : 0)
  return new Date(instant).toISOString()
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** Minutes Vienna runs ahead of UTC at that instant — 120 in summer, 60 in winter. */
function zoneOffsetMinutes(at: Date): number {
  const name =
    offsetInZone.formatToParts(at).find((p) => p.type === 'timeZoneName')
      ?.value ?? 'GMT+00:00'
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(name)
  if (!match) return 0
  const sign = match[1] === '-' ? -1 : 1
  return sign * (Number(match[2]) * 60 + Number(match[3]))
}
